import React, { useState, useMemo } from "react";
import { ColumnDef, VisibilityState, ColumnFiltersState, SortingState, RowSelectionState, ColumnSizingState } from "@tanstack/react-table";
import { type Device } from "@/hooks/useDevices";
import { getTimeAgo } from "@/utils/deviceStatus";
import { ExternalLink, RefreshCw, Power, Tags, X, ArrowUpDown, Lock, Unlock } from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons";
import { detectAppleDevice } from "@/utils/deviceModelDetection";
import StatusBadge from "./StatusBadge";
import CustomerTag from "./CustomerTag";
import { useSingleTenant } from "@/hooks/useSingleTenant";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { useNavigate } from "react-router-dom";
import { ActionsDropdown, ActionGroup } from "@/components/ui/actions-dropdown";
import { toast } from "sonner";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { DataTable } from "@/components/ui/data-table";
import { BulkActionToolbar } from "./device/BulkActionToolbar";
import { ColumnTagFilter } from "@/components/device/ColumnTagFilter";
import { ColumnMultiSelectFilter } from "@/components/device/ColumnMultiSelectFilter";
import { useEffect } from "react";
import * as tagsApi from "@/services/tagsApi";
import { mapApiTagMinimal } from "@/utils/tagMapping";
import { highlightMatch } from "@/utils/highlightMatch";
import * as devicesApi from "@/services/devicesApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

import { MappedTagMinimal } from "@/utils/tagMapping";
type Tag = MappedTagMinimal;

interface DeviceTableProps {
  devices: Device[];
  deviceTags?: Map<string, any[]>;
  searchQuery?: string;
}

const DeviceTable = ({ devices, deviceTags, searchQuery = "" }: DeviceTableProps) => {
  const navigate = useNavigate();
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [selectedDeviceForTags, setSelectedDeviceForTags] = useState<{ id: string; name: string } | null>(null);
  const [isTagRemovalOpen, setIsTagRemovalOpen] = useState(false);
  const [selectedDeviceForRemoval, setSelectedDeviceForRemoval] = useState<{ id: string; name: string } | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({ department: false, location: false });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    try {
      const saved = localStorage.getItem("deviceTableColumnFilters");
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("deviceTableColumnFilters", JSON.stringify(columnFilters));
    } catch {
      // ignore storage errors
    }
  }, [columnFilters]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const isSingleTenantUser = useSingleTenant();

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{
    type: "lock" | "unlock" | "unenroll" | "checkin";
    device: Device;
  } | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const getDeviceIcon = (device: Device) => {
    const { osType, variant } = detectAppleDevice(device.os_type || 'macOS', device.name, undefined, undefined);
    return <AppleDeviceIcon osType={osType} variant={variant as any} modelName={device.name} className="h-5 w-5 text-muted-foreground" />;
  };

  const getDeviceSerial = (device: Device) => {
    if (device.os_type?.toLowerCase().includes('mac')) {
      return `MB${device.serial_number?.substring(0, 3) || 'P14'}-${new Date(device.created_at).getFullYear() || '2023'}-${device.name?.substring(0, 3) || 'XX'}01`;
    }
    return device.serial_number || 'N/A';
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setIsActionLoading(true);
    try {
      const { type, device } = confirmAction;
      switch (type) {
        case "lock":
          await devicesApi.bulkLock({ deviceIds: [device.id] });
          toast.success(`Lock command sent to ${device.name}`);
          break;
        case "unlock":
          await devicesApi.bulkUnlock({ deviceIds: [device.id] });
          toast.success(`Unlock command sent to ${device.name}`);
          break;
        case "unenroll":
          await devicesApi.bulkUnenroll({ deviceIds: [device.id] });
          toast.success(`${device.name} has been unenrolled`);
          break;
        case "checkin":
          await devicesApi.checkInDevice({ uuid: device.id });
          toast.success(`Check-in triggered for ${device.name}`);
          break;
      }
    } catch (error) {
      console.error(`Error performing ${confirmAction.type}:`, error);
      toast.error(`Failed to ${confirmAction.type} device`);
    } finally {
      setIsActionLoading(false);
      setConfirmAction(null);
    }
  };

  const openTagAssignment = (device: Device) => { setSelectedDeviceForTags({ id: device.id, name: device.name }); setIsTagAssignmentOpen(true); };
  const openTagRemoval = (device: Device) => { setSelectedDeviceForRemoval({ id: device.id, name: device.name }); setIsTagRemovalOpen(true); };

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const apiTags = await tagsApi.listTags();
        setAvailableTags((apiTags || []).map((t: any) => mapApiTagMinimal(t)));
      } catch { /* silently fail */ }
    };
    fetchTags();
  }, []);

  const filterOptions = useMemo(() => {
    const statuses = new Set<string>(); const departments = new Set<string>();
    const locations = new Set<string>(); const customers = new Set<string>();
    const platforms = new Set<string>();
    devices.forEach(device => {
      statuses.add(device.status || 'pending');
      departments.add(device.department || '__null__');
      locations.add(device.location || '__null__');
      customers.add(device.customer || '__null__');
      platforms.add((device.platform || device.os_type || '__null__').toString().toUpperCase());
    });
    return {
      status: Array.from(statuses).sort().map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1), icon: <StatusBadge status={s} /> })),
      department: Array.from(departments).sort().map(d => ({ value: d, label: d === '__null__' ? 'Unassigned' : d, icon: <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center"><span className="text-xs text-muted-foreground">{d === '__null__' ? 'U' : d.charAt(0)}</span></div> })),
      location: Array.from(locations).sort().map(l => ({ value: l, label: l === '__null__' ? 'Unknown' : l })),
      customer: Array.from(customers).sort().map(c => ({ value: c, label: c === '__null__' ? 'Not Available' : c, icon: <CustomerTag customer={c === '__null__' ? 'Not Available' : c} /> })),
      platform: Array.from(platforms).sort().map(p => ({ value: p, label: p === '__NULL__' ? 'Unknown' : p })),
    };
  }, [devices]);

  const confirmDialogConfig = confirmAction ? {
    lock: { title: "Lock Device", description: `Are you sure you want to lock "${confirmAction.device.name}"? The device will be remotely locked.`, actionLabel: "Lock", loadingLabel: "Locking…", destructive: false },
    unlock: { title: "Unlock Device", description: `Are you sure you want to unlock "${confirmAction.device.name}"?`, actionLabel: "Unlock", loadingLabel: "Unlocking…", destructive: false },
    unenroll: { title: "Unenroll Device", description: `Are you sure you want to unenroll "${confirmAction.device.name}"? This action cannot be undone.`, actionLabel: "Unenroll", loadingLabel: "Unenrolling…", destructive: true },
    checkin: { title: "Check In Device", description: `Are you sure you want to trigger a check-in for "${confirmAction.device.name}"?`, actionLabel: "Check In", loadingLabel: "Checking In…", destructive: false },
  }[confirmAction.type] : null;

  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      { id: "select", header: ({ table }) => <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)} aria-label="Select all" />, cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} aria-label="Select row" />, enableSorting: false, enableHiding: false, size: 50 },
      { accessorKey: "name", header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Device Name<ArrowUpDown className="ml-2 h-4 w-4" /></Button>,
        cell: ({ row }) => (<div className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors" onClick={() => navigate(`/devices/${row.original.id}`)}>{getDeviceIcon(row.original)}<div className="flex-1 min-w-0"><div className="font-medium truncate">{row.original.name}</div><div className="text-sm text-muted-foreground truncate">{getDeviceSerial(row.original)}</div></div></div>), size: 250 },
      { id: "user", accessorFn: (row) => row.username || row.fullName || row.userEmail || '',
        header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">User<ArrowUpDown className="ml-2 h-4 w-4" /></Button>,
        cell: ({ row }) => {
          const name = row.original.username;
          const fullName = row.original.fullName;
          const email = row.original.userEmail;
          if (!name && !fullName && !email) return <span className="text-xs text-muted-foreground italic">Unassigned</span>;
          const primary = name || fullName;
          const showFullNameLine = fullName && name && fullName !== name;
          return (
            <div className="min-w-0">
              {primary && <div className="font-medium truncate">{highlightMatch(primary, searchQuery)}</div>}
              {showFullNameLine && <div className="text-xs text-muted-foreground truncate">{highlightMatch(fullName, searchQuery)}</div>}
              {email && <div className="text-sm text-muted-foreground truncate">{highlightMatch(email, searchQuery)}</div>}
            </div>
          );
        }, size: 200 },
      { accessorKey: "model", header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Model<ArrowUpDown className="ml-2 h-4 w-4" /></Button>,
        cell: ({ row }) => <span className="truncate font-mono text-sm">{row.original.model || row.original.modelName || '—'}</span>, size: 130 },
      { accessorKey: "platform", header: ({ column }) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Platform<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnMultiSelectFilter column={column} title="Filter by Platform" options={filterOptions.platform} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
        cell: ({ row }) => <span className="truncate uppercase text-sm">{row.original.platform || row.original.os_type?.toUpperCase() || '—'}</span>,
        filterFn: (row, _, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; const v = (row.original.platform || row.original.os_type || '__null__').toString().toUpperCase(); return filterValue.includes(v); }, enableColumnFilter: true, size: 120 },
      { accessorKey: "os_version", header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">OS Version<ArrowUpDown className="ml-2 h-4 w-4" /></Button>,
        cell: ({ row }) => <span className="truncate text-sm">{row.original.os_version || '—'}</span>, size: 110 },
      { id: "tags", accessorFn: (row) => { const tagList = deviceTags?.get(row.id) || []; return tagList.map(t => t.name).join(", "); },
        header: ({ column }) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Tags<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnTagFilter column={column} availableTags={availableTags} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
        cell: ({ row }) => { const tags = deviceTags?.get(row.original.id) || []; if (tags.length === 0) return <span className="text-xs text-muted-foreground italic">No tags</span>; return <TagsDisplay resourceId={row.original.id} resourceType="device" size="sm" maxTags={3} showRemoveButton={true} prefetchedTags={tags} />; },
        filterFn: (row, _, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; const rowTags = deviceTags?.get(row.original.id) || []; return filterValue.some(tagId => rowTags.map(t => t.id).includes(tagId)); }, enableColumnFilter: true, size: 200 },
      { accessorKey: "status", header: ({ column }) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Status<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnMultiSelectFilter column={column} title="Filter by Status" options={filterOptions.status} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
        cell: ({ row }) => <StatusBadge status={row.original.status || 'pending'} />, filterFn: (row, _, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; return filterValue.includes(row.original.status || 'pending'); }, enableColumnFilter: true, size: 120 },
      { accessorKey: "department", header: ({ column }) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Department<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnMultiSelectFilter column={column} title="Filter by Department" options={filterOptions.department} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
        cell: ({ row }) => <div className="flex items-center gap-2"><div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center"><span className="text-xs text-muted-foreground">{row.original.department?.charAt(0) || 'U'}</span></div><span className="truncate">{row.original.department || 'Unassigned'}</span></div>,
        filterFn: (row, _, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; return filterValue.includes(row.original.department || '__null__'); }, enableColumnFilter: true, size: 140 },
      { accessorKey: "location", header: ({ column }) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Location<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnMultiSelectFilter column={column} title="Filter by Location" options={filterOptions.location} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
        cell: ({ row }) => <span className="truncate">{row.original.location || 'Unknown'}</span>,
        filterFn: (row, _, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; return filterValue.includes(row.original.location || '__null__'); }, enableColumnFilter: true, size: 140 },
      ...(!isSingleTenantUser ? [
        { accessorKey: "customer", header: ({ column }: any) => { const fv = column.getFilterValue() as string[] | undefined; const isF = fv && fv.length > 0; return <div className="flex items-center gap-2"><Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Customer<ArrowUpDown className="ml-2 h-4 w-4" /></Button><ColumnMultiSelectFilter column={column} title="Filter by Customer" options={filterOptions.customer} />{isF && <div className="h-2 w-2 rounded-full bg-primary" />}</div>; },
          cell: ({ row }: any) => <CustomerTag customer={row.original.customer || 'Not Available'} />,
          filterFn: (row: any, _: any, filterValue: string[]) => { if (!filterValue || filterValue.length === 0) return true; return filterValue.includes(row.original.customer || '__null__'); }, enableColumnFilter: true, size: 140 },
        { accessorKey: "tenantId", header: "Tenant ID", cell: ({ row }: any) => <span className="truncate font-mono text-sm">{row.original.tenantId || 'Not Available'}</span>, filterFn: "includesString", size: 120 },
      ] as ColumnDef<Device>[] : []),
      { accessorKey: "last_seen", header: ({ column }) => <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="h-8 p-0 font-semibold">Last Seen<ArrowUpDown className="ml-2 h-4 w-4" /></Button>,
        cell: ({ row }) => <span className="text-sm">{getTimeAgo(row.original.last_seen || row.original.created_at)}</span>, size: 120 },
      { id: "actions", header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <ActionsDropdown
            actions={[
              { label: "View Device Details", icon: ExternalLink, onClick: () => navigate(`/devices/${row.original.id}`) },
              {
                label: "Management",
                items: [
                  { label: "Check In", icon: RefreshCw, onClick: () => setConfirmAction({ type: "checkin", device: row.original }) },
                  { label: "Assign Tags", icon: Tags, onClick: () => openTagAssignment(row.original) },
                  { label: "Remove Tags", icon: X, onClick: () => openTagRemoval(row.original) },
                ],
              } as ActionGroup,
              {
                label: "System",
                items: [
                  { label: "Lock Device", icon: Lock, onClick: () => setConfirmAction({ type: "lock", device: row.original }) },
                  { label: "Unlock Device", icon: Unlock, onClick: () => setConfirmAction({ type: "unlock", device: row.original }) },
                  { label: "Unenroll Device", icon: Power, onClick: () => setConfirmAction({ type: "unenroll", device: row.original }), destructive: true },
                ],
              } as ActionGroup,
            ]}
          />
        ), enableSorting: false, enableHiding: false, size: 100 },
    ], [navigate, deviceTags, availableTags, filterOptions, searchQuery]
  );

  const selectedDevices = Object.keys(rowSelection).length > 0 ? devices.filter((_, i) => rowSelection[i]) : [];

  return (
    <div className="space-y-4">
      {selectedDevices.length > 0 && <BulkActionToolbar selectedDevices={selectedDevices} onClearSelection={() => setRowSelection({})} onRefresh={() => toast.success(`Refreshing ${selectedDevices.length} devices`)} />}
      <DataTable data={devices} columns={columns} enableRowSelection={true} enableColumnResizing={true} rowSelection={rowSelection} onRowSelectionChange={setRowSelection} columnVisibility={columnVisibility} onColumnVisibilityChange={setColumnVisibility} columnFilters={columnFilters} onColumnFiltersChange={setColumnFilters} sorting={sorting} onSortingChange={setSorting} columnSizing={columnSizing} onColumnSizingChange={setColumnSizing} emptyMessage="No devices found." />
      {selectedDeviceForTags && <TagAssignmentDialog open={isTagAssignmentOpen} onOpenChange={setIsTagAssignmentOpen} resourceId={selectedDeviceForTags.id} resourceType="device" resourceName={selectedDeviceForTags.name} onAssignmentComplete={() => setSelectedDeviceForTags(null)} />}
      {selectedDeviceForRemoval && <TagRemovalDialog open={isTagRemovalOpen} onOpenChange={setIsTagRemovalOpen} deviceId={selectedDeviceForRemoval.id} deviceName={selectedDeviceForRemoval.name} onRemovalComplete={() => setSelectedDeviceForRemoval(null)} />}

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open && !isActionLoading) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialogConfig?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialogConfig?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isActionLoading}
              onClick={async (e) => { e.preventDefault(); await handleConfirmAction(); }}
              className={confirmDialogConfig?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {isActionLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{confirmDialogConfig?.loadingLabel}</>
              ) : confirmDialogConfig?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeviceTable;
